import { RecordKind } from '../record.js';
import { Product, ProductGroup } from '../product.js';
import { Option, OptionGroup, SourcedValue } from '../option.js';
import { PropertyPath } from '../engine.js';
import { equableTypeGuard, MLString } from '../types.js';

// import { h64 } from 'xxhashjs';
// import { xxHash32 } from 'js-xxhash';
// import { MD5 } from 'object-hash';
// import deepEqual from 'deep-equal'
import fastDeepEqual from 'fast-deep-equal';
import { IPropertyChange, PropertyChanges } from './notifications/LookupMutatorCorrectionEvent.js';
import { LanguageService } from './LanguageService.js';

export class EngineUtils {
  private static readonly seed = 0xABCD;
  static hash = (v: any) => {
    // return MD5(v);
    return JSON.stringify(v);
    // const value = xxHash32(v, EngineUtils.seed).toString();
    // return value;
  }

  flattenProducts(root: ProductGroup): Product[] {
    let list: Product[] = [];
    for (let child of root.children) {
      if (child.kind === RecordKind.Product) {
        list.push(child as Product);
      } else {
        list.push(...this.flattenProducts(child as ProductGroup))
      }
    }
    return list;
  }

  flattenOptionGroup(root: OptionGroup): Option[] {
    let list: Option[] = [];
    for (let child of root.children) {
      if (child.kind === RecordKind.Option) {
        list.push(child as Option);
      } else {
        list.push(...this.flattenOptionGroup(child as OptionGroup))
      }
    }
    return list;
  }

  flattenOptions<T>(root: OptionGroup[] | Option<T>[]): Option<T>[] {
    const result = [];
    root.forEach(next => {
      if (next.kind === RecordKind.OptionGroup) {
        result.push(...this.flattenOptionGroup(next));
      } else {
      }
      if (next.kind === RecordKind.Option) {
        result.push(next);
      }
    })
    return result;
  }

  getAvailableValues<T, V>(item: T, selector: (item: T) => V): Option<V>[] {
    const potentialSourcedValue = selector(item);
    if (potentialSourcedValue instanceof SourcedValue) {
      return this.flattenOptions(potentialSourcedValue.getSource());
    } else {
      return [];
    }
  }

  flattenSourcedValues<T, V>(list: T[], selector: (obj: T) => V): Option<V>[] {
    const result: Option<V>[] = [];
    for (let item of list) {
      const options = this.getAvailableValues(item, selector);
      result.push(...options);
    }
    return result;
  }

  cartesianProduct<Option>(optionsPotentialValues: Map<PropertyPath, Option[]>,
                           handler: (
                             tuple: { optionPropertyPath: PropertyPath, option: Option }[]
                           ) => boolean | void) {
    if (!optionsPotentialValues.size) {
      handler([]);
      return;
    }
    const vector = [];
    const options: { optionPropertyPath: PropertyPath, optionValues: Option[] }[] = [];
    let i = 0;
    for (const [optionPropertyPath, optionValues] of optionsPotentialValues) {
      vector[i] = 0;
      options[i] = { optionPropertyPath, optionValues };
      i++;
    }

    let j;
    while (true) {
      const values = options.map((pair, i) => {
        return ({
          optionPropertyPath: pair.optionPropertyPath,
          option: pair.optionValues[vector[i]]
        });
      })
      const exitExpected = handler(values);
      if (exitExpected === true) {
        return;
      }
      j = vector.length - 1;
      while (true) {
        vector[j]++;
        if (vector[j] < options[j].optionValues.length) {
          break;
        }
        vector[j] = 0;
        j--;
        if (j < 0) {
          return;
        }
      }
    }
  }

  cartesian<T, V>(initials: T[],
                  fns: Array<(value: T, q: T[][]) => T[]>,
                  finalizer: (value: T, q: T[][]) => V,
                  breakHandler: (index: number, q: T[][]) => void = () => {}): V[] {
    const q = [initials.slice()];
    const result: V[] = [];
    while (q.length) {
      const next = q.length - 1;
      const nextGroup = q[next];
      if (nextGroup.length > 0) {
        const value = nextGroup.shift();
        if (next >= fns.length) {
          result.push(finalizer(value, q));
        } else {
          const values = fns[next](value, q);
          q.push(values);
        }
      } else {
        breakHandler(next, q);
        q.pop();
      }
    }
    return result;
  }

  equals(left: any, right: any) {
    if (left && right && equableTypeGuard(left) && equableTypeGuard(right)) {
      return left.eq(right);
    }
    if (typeof left === 'object' || typeof right === 'object') {
      return fastDeepEqual(left, right);
    }
    return left == right;
  }

  getPropertyPathValue(obj: unknown, propertyPath: PropertyPath) {
    const names = propertyPath.split('.');
    let currentValue = obj;
    for (let name of names) {
      if (currentValue !== undefined) {
        currentValue = currentValue[name];
      }
    }
    return currentValue;
  }

  objectDiff(oldObject: { [p: string]: any }, newObject: { [p: string]: any }, properties: PropertyPath[] = []): PropertyChanges {
    let result: PropertyChanges = {};
    for (const prop of properties) {
      const oldValue = this.getPropertyPathValue(oldObject, prop);
      const newValue = this.getPropertyPathValue(newObject, prop);
      if (oldValue === undefined) {
        result[prop] = {
          oldValue: null,
          newValue: newValue
        }
        continue;
      }

      if (newValue === undefined) {
        result[prop] = {
          oldValue: oldValue,
          newValue: null
        }
        continue;
      }
      if (!this.equals(oldValue, newValue)) {
        result[prop] = {
          oldValue: oldValue,
          newValue: newValue
        }
      }
    }
    return result;
  }

  distinct<T>(data: T[], columns: (string | ColumnSelector<T>)[]) {
    const dataset = [];
    const visited = new Set<string>()
    for (let item of data) {
      const record = [];
      for (let i = 0; i < columns.length; i++) {
        let column = columns[i];
        if (typeof column === 'string') {
          record[i] = item[column];
        } else {
          record[i] = column(item);
        }
      }
      if (!isRecordInDataset(visited, record, dataset)) {
        dataset.push(record);
        visited.add(getHash(record));
      }
    }
    return dataset;
  }

  groupBy<T>(items: T[], keySelector: (item: T) => string): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (let item of items) {
      const key = keySelector(item);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(item);
    }
    return map;
  }

  getPropertyPathValues(model: object, properties: PropertyPath[]) {
    const result = [];
    for (const property of properties) {
      result.push(this.getPropertyPathValue(model, property));
    }
    return result;
  }

  buildObjectByKeys(value: any[], keys: string[]) {
    if (value.length !== keys.length) {
      throw new Error('Invalid number of columns')
    }
    let obj = {};
    for (let j = 0; j < value.length; j++) {
      obj[keys[j]] = value[j];
    }
    return obj;
  }

  compareStrings(itemA: string, itemB: string, ignoreCase = false) {
    if (ignoreCase) {
      itemA = itemA.toUpperCase();
      itemB = itemB.toUpperCase();
    }

    const itemAParsedInt = Number.parseInt(itemA, 10);
    const itemBParsedInt = Number.parseInt(itemB, 10);

    if (Number.isFinite(itemAParsedInt) &&
      Number.isFinite(itemBParsedInt) &&
      itemAParsedInt !== itemBParsedInt) {
      return itemAParsedInt > itemBParsedInt ? 1 : -1;
    }

    if (itemA.length !== itemB.length) {
      let equalSubstring = '';
      let charIndex = 0;
      while (itemA[charIndex] === itemB[charIndex]) {
        equalSubstring += itemA[charIndex];
        charIndex++;
      }
      const equalSubstringLength = equalSubstring.length;
      if (equalSubstringLength) {
        const diffSubstringA = itemA.substr(equalSubstringLength);
        const diffSubstringB = itemB.substr(equalSubstringLength);
        const diffSubstringAParsedInt = Number.parseInt(diffSubstringA, 10);
        const diffSubstringBParsedInt = Number.parseInt(diffSubstringB, 10);
        if (diffSubstringAParsedInt && diffSubstringBParsedInt) {
          return diffSubstringAParsedInt > diffSubstringBParsedInt ? 1 : -1;
        }
      }
    }

    return itemA === itemB ? 0 : (itemA > itemB ? 1 : -1);
  }

  checkIfMLStringAndCompare(languageService: LanguageService, itemA: MLString | string, itemB: MLString | string, ignoreCase: boolean = false) {
    const valueA = itemA instanceof MLString ? languageService.translateOptional(itemA) : itemA;
    const valueB = itemB instanceof MLString ? languageService.translateOptional(itemB) : itemB;
    return engineUtils.compareStrings(valueA, valueB, ignoreCase);
  }

  pipe<T>(input: T, ...fns: Fn<T>[]): T {
    let result = input;
    for (let fn of fns) {
      result = fn(result);
    }
    return result;
  }

  fromFirstOrDefault<T, V>(xs: T[], selector: (x: T) => V, defaultValue: V): V {
    if (xs.length) {
      return selector(xs[0]);
    } else return defaultValue;
  }

  clearArray<T>(xs: T[]): T[] {
    while (xs.length > 0) {
      xs.pop();
    }
    return xs;
  }
}

type Fn<T> = (input: T) => T;

function isRecordInDataset(visited: Set<string>, record: object[], dataset: object[]) {
  return visited.has(this.getHash(record));
}

function getHash(record: object[]) {
  return record.join('##')
}

export const engineUtils = new EngineUtils();
export type ColumnSelector<T> = (v: T) => string | number | boolean;
